// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 16d243c4-e33d-439f-803e-ccc87cbe93d1

// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// ---------------------------------------------------------------------------
// Primordia — Business Capability Map
// ---------------------------------------------------------------------------
// The LeanIX spine: what the business DOES, as a hierarchy, with Applications
// mapped onto it — so rationalisation can be argued from capabilities ("do we
// need three systems supporting Claims & Billing?") instead of a flat
// software list.
//
// This screen is a MAP, not an editor. Creating a capability drops a draft
// fact sheet with the normal store.createFactSheet path (same as Inventory's
// "New"); everything else — renaming, describing, mapping an application onto
// a capability, setting its parent — happens on the fact sheet itself via the
// existing RelationEditor, exactly like every other relationship in the
// product. Building a second, parallel editing surface here would be the same
// mistake FactSheetDetail already solved once.
//
// Design rules this screen holds itself to, carried over from LandscapeReport
// and MindMap:
//   * A capability with nothing mapped to it is a real, informative state —
//     "(none mapped)" — never hidden and never silently dropped from the tree.
//   * An unmapped application is a work-queue item, not a footnote, so it is
//     always visible, never behind a click a user has to know to make.
//   * The hierarchy is read from ONE relation (`parentCapability`, single) —
//     see the comment in schema.js next to it for why there is no separate
//     `childCapabilities` edge to keep in sync.
//   * A cycle in that relation must not hang the renderer. The parent chain
//     is a "functional graph" (every node has at most one parent), so cycle
//     detection is a single linear pass with node colouring, independent of
//     how many capabilities exist.
//   * Rendering is capped, not merely trusted to be small — see MAX_TREE_ROWS.
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Button, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Tooltip, Divider, Alert,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { PICKLISTS } from './schema';
import { TypeBadge } from './ui';
import { getSession, isMaster } from './auth';
import store from './store';
import HelpBubble from './HelpBubble';

const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEADER = 'sectionHeader.main';

const NOT_SET = 'Not set';
const NOT_SET_COLOR = '#9AA6B2';

// Render ceilings. Neither is a data limit — every capability and every
// unmapped application is always counted correctly; these bound how much gets
// PAINTED, the same split LandscapeReport and MindMap draw.
const MAX_TREE_ROWS = 150;       // flattened, visible (post-collapse) tree rows
const MAX_APPS_IN_PANEL = 100;   // applications listed for one selected node
const MAX_UNMAPPED_SHOWN = 100;  // unmapped-applications work queue

// red -> amber -> green, same stops as LandscapeReport (not exported there).
const scaleColor = (idx, n) => {
  const stops = ['#D0021B', '#F5A623', '#F8E71C', '#7ED321', '#2E7D32'];
  if (n <= 1) return stops[4];
  const t = idx / (n - 1);
  return stops[Math.round(t * (stops.length - 1))];
};
const TIME_COLORS = { Invest: '#2E7D32', Tolerate: '#0B5FFF', Migrate: '#F5A623', Eliminate: '#D0021B' };
const PCT_BANDS = ['0%', '1–39%', '40–74%', '≥75%'];
const bandPercent = (v) => {
  const n = Number(v);
  if (v === undefined || v === null || v === '' || !Number.isFinite(n)) return NOT_SET;
  if (n <= 0) return PCT_BANDS[0];
  if (n < 40) return PCT_BANDS[1];
  if (n < 75) return PCT_BANDS[2];
  return PCT_BANDS[3];
};

// The selectable heat dimensions. `get` reads off the (Application) fact
// sheet; `color` turns a value into a swatch colour using the same idiom as
// LandscapeReport: semantic map where one exists, otherwise an ordinal scale
// walking the picklist order, "Not set" always the neutral grey.
const HEAT_DIMENSIONS = [
  {
    key: 'timeClassification', label: 'TIME Classification',
    get: (a) => a.fields?.timeClassification || NOT_SET,
    color: (v) => (v === NOT_SET ? NOT_SET_COLOR : TIME_COLORS[v] || NOT_SET_COLOR),
  },
  {
    key: 'sixRClassification', label: '6R Classification',
    get: (a) => a.fields?.sixRClassification || NOT_SET,
    color: (v) => {
      if (v === NOT_SET) return NOT_SET_COLOR;
      const i = PICKLISTS.sixR.indexOf(v);
      return i >= 0 ? scaleColor(i, PICKLISTS.sixR.length) : NOT_SET_COLOR;
    },
  },
  {
    key: 'functionalFit', label: 'Functional Fit',
    get: (a) => a.fields?.functionalFit || NOT_SET,
    color: (v) => {
      if (v === NOT_SET) return NOT_SET_COLOR;
      const i = PICKLISTS.functionalFit.indexOf(v);
      return i >= 0 ? scaleColor(i, PICKLISTS.functionalFit.length) : NOT_SET_COLOR;
    },
  },
  {
    key: 'completion', label: 'Data Completion',
    get: (a) => bandPercent(a.completion),
    color: (v) => {
      if (v === NOT_SET) return NOT_SET_COLOR;
      const i = PCT_BANDS.indexOf(v);
      return i >= 0 ? scaleColor(i, PCT_BANDS.length) : NOT_SET_COLOR;
    },
  },
];

// ---------------------------------------------------------------------------
// Starter set — a clearly-labelled STARTING POINT, not a prescription. Kept to
// ~15 records (7 top-level + 8 child) so it reads as scaffolding a client
// engagement immediately reshapes, not as Optimum's opinion of the "right"
// capability model. Names/groupings are the common ground across US health
// system capability maps (Patient Care, Revenue Cycle, clinical ancillary
// services, corporate ERP, population health, patient access, and IT/security
// as a capability in its own right), not any one client's actual structure.
// ---------------------------------------------------------------------------
const STARTER_CAPABILITIES = [
  { name: 'Patient Care', parent: null, description: 'Direct clinical care delivery across the continuum — the reason the rest of the capability map exists.' },
  { name: 'Ambulatory Care', parent: 'Patient Care', description: 'Outpatient visits, clinics and physician practices.' },
  { name: 'Inpatient Care', parent: 'Patient Care', description: 'Admitted patient care, nursing units and inpatient orders.' },

  { name: 'Revenue Cycle', parent: null, description: 'Everything from scheduling a patient through getting paid for their care.' },
  { name: 'Patient Access & Registration', parent: 'Revenue Cycle', description: 'Scheduling, registration, eligibility and pre-authorization.' },
  { name: 'Claims & Billing', parent: 'Revenue Cycle', description: 'Charge capture, coding, claims submission and patient billing.' },

  { name: 'Clinical Ancillaries', parent: null, description: 'Diagnostic and therapeutic services ordered as part of a care plan.' },
  { name: 'Laboratory & Pathology', parent: 'Clinical Ancillaries', description: 'Lab and pathology ordering, specimen tracking and results.' },
  { name: 'Radiology & Imaging', parent: 'Clinical Ancillaries', description: 'Imaging orders, PACS and diagnostic imaging workflow.' },

  { name: 'ERP & Corporate Services', parent: null, description: 'The business-of-the-business functions common to any large employer.' },
  { name: 'Finance & Accounting', parent: 'ERP & Corporate Services', description: 'General ledger, AP/AR, budgeting and financial reporting.' },
  { name: 'Human Resources', parent: 'ERP & Corporate Services', description: 'Workforce management, payroll, credentialing and staff scheduling.' },

  { name: 'Population Health', parent: null, description: 'Care management, quality reporting and value-based care programs across a patient panel.' },

  { name: 'Access & Engagement', parent: null, description: 'How patients and referring providers find, reach and interact with the system.' },
  { name: 'Patient Portal & Digital Front Door', parent: 'Access & Engagement', description: 'Self-service scheduling, messaging, bill pay and patient-facing digital tools.' },

  { name: 'IT & Security', parent: null, description: 'The technology function itself, treated as a business capability like any other.' },
  { name: 'Cybersecurity & Compliance', parent: 'IT & Security', description: 'Security operations, risk management and regulatory/compliance obligations (HIPAA and beyond).' },
];

// ---------------------------------------------------------------------------
// Tree construction with cycle guard.
//
// The parent relation gives every capability AT MOST ONE parent, so the graph
// of child->parent edges is a "functional graph": walking parent pointers from
// any node either reaches a root (parent === null) or eventually revisits a
// node already on the current walk, which is the cycle. Standard 3-colour DFS
// (white/gray/black) finds every cycle in one linear pass regardless of how
// many capabilities exist or how they are shaped — there is no path here that
// re-walks the same node twice, so nothing here can hang.
// ---------------------------------------------------------------------------
function buildTree(caps) {
  const byId = new Map(caps.map((c) => [c.id, c]));
  const rawParentOf = new Map();
  caps.forEach((c) => {
    const rel = (c.relations && c.relations.parentCapability) || [];
    const p = rel[0];
    // A dangling parent id (the target was deleted) reads exactly like "no
    // parent" — an orphan, not an error. It becomes a root, same as any
    // capability that never had a parent set.
    rawParentOf.set(c.id, (p && p.id !== c.id && byId.has(p.id)) ? p.id : null);
  });

  const state = new Map(); // id -> 1 (in progress) | 2 (resolved, acyclic)
  const cycleMembers = new Set();
  const cycles = [];
  caps.forEach((c) => {
    if (state.get(c.id)) return;
    const path = [];
    let cur = c.id;
    while (cur != null) {
      const st = state.get(cur);
      if (st === 2) break;               // walked into an already-resolved chain
      if (st === 1) {
        // "in progress" means either (a) a genuine back-edge into THIS walk's
        // own path — a new cycle — or (b) this walk merely feeds into a cycle
        // a PRIOR walk already found and left permanently marked in-progress.
        // Only (a) is a cycle; for (b), `cur` is not in `path` at all, and
        // slicing from indexOf's -1 would silently fabricate a fake one-node
        // "cycle" out of whatever this unrelated path happened to contain.
        const idx = path.indexOf(cur);
        if (idx !== -1) {
          const cyc = path.slice(idx);
          cycles.push(cyc);
          cyc.forEach((id) => cycleMembers.add(id));
        }
        break;
      }
      state.set(cur, 1);
      path.push(cur);
      cur = rawParentOf.get(cur);
    }
    path.forEach((id) => { if (!cycleMembers.has(id)) state.set(id, 2); });
  });

  // A capability inside a cycle is rendered as its own top-level item — its
  // "true" position in the hierarchy is undefined until someone breaks the
  // loop on its fact sheet, and refusing to place it would mean hiding a
  // record, which nothing in this product does.
  const effectiveParent = (id) => (cycleMembers.has(id) ? null : rawParentOf.get(id));

  const childrenOf = new Map();
  caps.forEach((c) => {
    const p = effectiveParent(c.id);
    if (p == null) return;
    if (!childrenOf.has(p)) childrenOf.set(p, []);
    childrenOf.get(p).push(c.id);
  });
  const roots = caps.filter((c) => effectiveParent(c.id) == null).map((c) => c.id);

  return { byId, childrenOf, roots, cycles, cycleMembers };
}

// Depth-first, alphabetical at each level, respecting a collapsed-id set. No
// truncation here — that happens once, at the call site, against the total —
// so the "N more" count is always the true remainder, not just what one
// subtree happened to overflow into.
function flattenVisible(tree, collapsed) {
  const byName = (a, b) => (tree.byId.get(a).name || '').localeCompare(tree.byId.get(b).name || '');
  const rows = [];
  const stack = [...tree.roots].sort(byName).reverse().map((id) => ({ id, depth: 0 }));
  // Guard against a construction bug rather than trusting the forest property
  // implied by buildTree() — cheap, and it is the difference between "renders
  // slightly wrong" and "tab hangs" if that invariant is ever broken later.
  const guard = tree.byId.size + 1;
  let steps = 0;
  while (stack.length && steps <= guard) {
    steps += 1;
    const { id, depth } = stack.pop();
    rows.push({ id, depth });
    if (!collapsed.has(id)) {
      const kids = (tree.childrenOf.get(id) || []).slice().sort(byName);
      for (let i = kids.length - 1; i >= 0; i -= 1) stack.push({ id: kids[i], depth: depth + 1 });
    }
  }
  return rows;
}

// Applications mapped directly onto each capability (before roll-up).
function directAppsByCapability(caps, apps) {
  const map = new Map();
  caps.forEach((c) => map.set(c.id, []));
  apps.forEach((a) => {
    ((a.relations && a.relations.businessCapabilities) || []).forEach((r) => {
      if (map.has(r.id)) map.get(r.id).push(a);
    });
  });
  return map;
}

// Roll-up: a capability's own apps plus every descendant's, deduped by app id
// (an app mapped to both a parent and one of its children counts once).
// Memoised per node so the whole tree is one pass, not one per selection.
function rollupApps(id, childrenOf, directMap, cache) {
  if (cache.has(id)) return cache.get(id);
  const seen = new Map();
  (directMap.get(id) || []).forEach((a) => seen.set(a.id, a));
  (childrenOf.get(id) || []).forEach((childId) => {
    rollupApps(childId, childrenOf, directMap, cache).forEach((a) => seen.set(a.id, a));
  });
  const out = [...seen.values()];
  cache.set(id, out);
  return out;
}

// Dominant value + full breakdown for a set of apps under one dimension —
// the LeanIX "which colour paints this tile" question.
function heatFor(dimension, apps) {
  const counts = new Map();
  apps.forEach((a) => {
    const v = dimension.get(a);
    counts.set(v, (counts.get(v) || 0) + 1);
  });
  let dominant = NOT_SET;
  let best = -1;
  counts.forEach((n, v) => {
    // Real values outrank "Not set" at an equal count — an evenly-split node
    // should read by what IS known, not fall back to the least useful bucket.
    if (n > best || (n === best && v !== NOT_SET && dominant === NOT_SET)) { best = n; dominant = v; }
  });
  const breakdown = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  return { dominant, breakdown, total: apps.length };
}

function AppChip({ app, onOpen }) {
  return (
    <Chip
      size="small"
      icon={<Box sx={{ ml: 0.5 }}><TypeBadge type="Application" size={16} /></Box>}
      label={(
        <span>
          {app.name}
          {app.fields?.timeClassification && <span style={{ opacity: 0.7 }}> · {app.fields.timeClassification}</span>}
          {app.fields?.sixRClassification && <span style={{ opacity: 0.7 }}> · {app.fields.sixRClassification}</span>}
        </span>
      )}
      onClick={() => onOpen(app.id)}
      sx={{ cursor: 'pointer', mr: 0.5, mb: 0.5, bgcolor: '#eef4fb' }}
    />
  );
}

// One row of the flattened tree.
function TreeRow({ node, depth, hasChildren, collapsed, onToggle, count, heat, selected, onSelect }) {
  const swatch = count === 0 ? NOT_SET_COLOR : heat.color;
  return (
    <Box
      onClick={() => onSelect(node.id)}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, py: 0.6, pr: 1,
        pl: 1 + depth * 2.5, cursor: 'pointer', borderRadius: 1,
        bgcolor: selected ? '#eef4fb' : 'transparent',
        '&:hover': { bgcolor: selected ? '#eef4fb' : '#f7f9fb' },
      }}
    >
      <IconButton
        size="small"
        sx={{ visibility: hasChildren ? 'visible' : 'hidden', width: 22, height: 22 }}
        onClick={(e) => { e.stopPropagation(); onToggle(node.id); }}
        aria-label={collapsed ? 'Expand' : 'Collapse'}
      >
        {collapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </IconButton>
      <TypeBadge type="BusinessCapability" size={18} />
      <Typography sx={{ fontWeight: depth === 0 ? 700 : 600, fontSize: depth === 0 ? 14 : 13.5, color: HEADER }}>
        {node.name}
      </Typography>
      <Box
        title={count === 0 ? '(none mapped)' : `${heat.dominant} — ${heat.breakdown.map(([v, n]) => `${v}: ${n}`).join(' · ')}`}
        sx={{
          width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
          bgcolor: swatch,
          border: count === 0 ? '1px dashed #5C6B7A' : '1px solid transparent',
        }}
      />
      <Chip
        size="small"
        label={count === 0 ? '(none mapped)' : `${count} app${count === 1 ? '' : 's'}`}
        sx={{
          height: 18, fontSize: 10.5, fontWeight: 600,
          color: count === 0 ? GREY : HEADER,
          fontStyle: count === 0 ? 'italic' : 'normal',
          bgcolor: count === 0 ? 'transparent' : '#eef4fb',
          border: count === 0 ? '1px dashed #cfd6dd' : 'none',
        }}
      />
    </Box>
  );
}

export default function CapabilityMap({ tenantId, onOpen }) {
  const [caps, setCaps] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [selectedId, setSelectedId] = useState(null);
  const [dimensionKey, setDimensionKey] = useState('timeClassification');
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [starterOpen, setStarterOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [unmappedLimit, setUnmappedLimit] = useState(MAX_UNMAPPED_SHOWN);

  const me = getSession();
  const iAmMaster = isMaster();
  const iAmAdmin = iAmMaster || (me && me.roleId === 'admin');
  // Master's workspace holds no records of its own, and listFactSheets('optimum', …)
  // returns EVERY client's rows — grouping those into one tree here would be
  // exactly the cross-tenant blend CLAUDE.md's isolation rule warns about, so
  // this screen simply refuses to build one, the same way Inventory refuses to
  // author from master.
  const isMasterWorkspace = tenantId === 'optimum';

  useEffect(() => {
    if (isMasterWorkspace) { setLoading(false); return undefined; }
    let alive = true;
    setLoading(true);
    setError('');
    Promise.all([
      store.listFactSheets(tenantId, { type: 'BusinessCapability' }),
      store.listFactSheets(tenantId, { type: 'Application' }),
    ]).then(([c, a]) => {
      if (!alive) return;
      setCaps(c || []);
      setApps(a || []);
      setLoading(false);
    }).catch((e) => {
      if (!alive) return;
      setError(String((e && e.message) || e));
      setLoading(false);
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, isMasterWorkspace, refreshKey]);

  const reload = () => setRefreshKey((k) => k + 1);

  const tree = useMemo(() => buildTree(caps), [caps]);
  const directMap = useMemo(() => directAppsByCapability(caps, apps), [caps, apps]);
  const rollupCache = useMemo(() => {
    const cache = new Map();
    caps.forEach((c) => rollupApps(c.id, tree.childrenOf, directMap, cache));
    return cache;
  }, [caps, tree.childrenOf, directMap]);

  const dimension = HEAT_DIMENSIONS.find((d) => d.key === dimensionKey) || HEAT_DIMENSIONS[0];

  const flatRows = useMemo(() => flattenVisible(tree, collapsed), [tree, collapsed]);
  const visibleRows = flatRows.slice(0, MAX_TREE_ROWS);
  const overflowRows = flatRows.length - visibleRows.length;

  const unmappedApps = useMemo(
    () => apps.filter((a) => !((a.relations && a.relations.businessCapabilities) || []).length),
    [apps],
  );

  const toggle = (id) => setCollapsed((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const selected = selectedId ? tree.byId.get(selectedId) : null;
  const selectedDirect = selectedId ? (directMap.get(selectedId) || []) : [];
  const selectedRollup = selectedId ? (rollupCache.get(selectedId) || []) : [];
  const selectedChildren = selectedId ? (tree.childrenOf.get(selectedId) || []).map((id) => tree.byId.get(id)) : [];

  const canAuthorHere = !isMasterWorkspace;

  const createCapability = async () => {
    if (!newName.trim()) return;
    const fs = await store.createFactSheet(tenantId, 'BusinessCapability', newName.trim());
    setNewOpen(false);
    setNewName('');
    reload();
    onOpen(fs.id);
  };

  const seedStarter = async () => {
    setSeeding(true);
    try {
      const idByName = {};
      // Top-level first, sequentially: children need a real parent id to link
      // to, so this cannot be a Promise.all.
      // eslint-disable-next-line no-restricted-syntax
      for (const cap of STARTER_CAPABILITIES) {
        // eslint-disable-next-line no-await-in-loop
        const fs = await store.createFactSheet(tenantId, 'BusinessCapability', cap.name);
        // eslint-disable-next-line no-await-in-loop
        await store.saveFactSheet({ ...fs, fields: { ...fs.fields, description: cap.description } });
        idByName[cap.name] = fs.id;
      }
      // eslint-disable-next-line no-restricted-syntax
      for (const cap of STARTER_CAPABILITIES) {
        if (!cap.parent) continue;
        const id = idByName[cap.name];
        const parentId = idByName[cap.parent];
        if (!id || !parentId) continue;
        // eslint-disable-next-line no-await-in-loop
        const fs = await store.getFactSheet(id);
        // eslint-disable-next-line no-await-in-loop
        await store.saveFactSheet({
          ...fs,
          relations: { ...(fs.relations || {}), parentCapability: [{ id: parentId }] },
        });
      }
    } finally {
      setSeeding(false);
      setStarterOpen(false);
      reload();
    }
  };

  if (isMasterWorkspace) {
    return (
      <Box sx={{ p: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: 700, color: HEADER, mb: 0.5 }}>
            Business Capability Map is per-client
          </Typography>
          <Typography variant="body2" sx={{ color: GREY }}>
            The optimum workspace holds no records of its own, and a capability hierarchy only means something
            inside one client&rsquo;s estate. Switch to a client workspace with the selector in the top bar.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <AccountTreeIcon sx={{ color: BLUE }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEADER }}>Business Capability Map</Typography>
        <HelpBubble topic="capabilityMap" />
        <Box sx={{ flex: 1 }} />
        <TextField
          select size="small" label="Colour by" value={dimensionKey}
          onChange={(e) => setDimensionKey(e.target.value)} sx={{ minWidth: 190 }}
        >
          {HEAT_DIMENSIONS.map((d) => <MenuItem key={d.key} value={d.key}>{d.label}</MenuItem>)}
        </TextField>
        <Tooltip title={canAuthorHere ? '' : 'Switch to a client workspace to create records.'}>
          <span>
            <Button startIcon={<AddIcon />} variant="outlined" disabled={!canAuthorHere} onClick={() => setNewOpen(true)}>
              New capability
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 2 }}>
        Click a capability to see what maps to it. Creating and editing capabilities, mapping an application onto
        one, and setting a parent all happen on the fact sheet itself (the pencil icon on any node) — this screen
        is the map, not the editor.
      </Typography>

      {loading && <Paper sx={{ p: 4, textAlign: 'center', color: GREY }}>Loading capabilities…</Paper>}

      {!loading && !!error && (
        <Paper sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: 700, color: '#8a2118', mb: 0.5 }}>Could not load this workspace</Typography>
          <Typography variant="body2" sx={{ color: GREY }}>{error}</Typography>
        </Paper>
      )}

      {!loading && !error && !caps.length && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 700, color: HEADER, mb: 1 }}>
            No Business Capability fact sheets yet
          </Typography>
          <Typography variant="body2" sx={{ color: GREY, mb: 2, maxWidth: 520, mx: 'auto' }}>
            A capability map starts empty. Add capabilities one at a time, or — since this workspace has none at
            all — start from a healthcare capability set and reshape it for this client.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center">
            <Button startIcon={<AddIcon />} variant="outlined" disabled={!canAuthorHere} onClick={() => setNewOpen(true)}>
              New capability
            </Button>
            {iAmAdmin && (
              <Button startIcon={<AutoAwesomeIcon />} variant="contained" disabled={!canAuthorHere}
                sx={{ bgcolor: NAVY, '&:hover': { bgcolor: BLUE } }} onClick={() => setStarterOpen(true)}>
                Start from a healthcare capability set
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {!loading && !error && !!caps.length && (
        <>
          {!!tree.cycles.length && (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                {tree.cycles.length} circular parent reference{tree.cycles.length === 1 ? '' : 's'} found —
                shown as top-level items until fixed
              </Typography>
              {tree.cycles.map((cyc, i) => (
                <Typography key={i} variant="body2" sx={{ fontSize: 12.5 }}>
                  {cyc.map((id, j) => (
                    <React.Fragment key={id}>
                      {j > 0 && ' → '}
                      <span
                        style={{ color: BLUE, cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => onOpen(id)}
                      >
                        {tree.byId.get(id)?.name || id}
                      </span>
                    </React.Fragment>
                  ))}
                  {' → '}{tree.byId.get(cyc[0])?.name}
                </Typography>
              ))}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Paper sx={{ flex: 2, minWidth: 340, p: 1.5, maxHeight: '70vh', overflowY: 'auto' }}>
              {visibleRows.map(({ id, depth }) => {
                const node = tree.byId.get(id);
                const hasChildren = !!(tree.childrenOf.get(id) || []).length;
                const rollup = rollupCache.get(id) || [];
                const heat = heatFor(dimension, rollup);
                return (
                  <TreeRow
                    key={id}
                    node={node}
                    depth={depth}
                    hasChildren={hasChildren}
                    collapsed={collapsed.has(id)}
                    onToggle={toggle}
                    count={rollup.length}
                    heat={{ dominant: heat.dominant, breakdown: heat.breakdown, color: dimension.color(heat.dominant) }}
                    selected={selectedId === id}
                    onSelect={setSelectedId}
                  />
                );
              })}
              {overflowRows > 0 && (
                <Typography variant="caption" sx={{ display: 'block', color: GREY, mt: 1, fontStyle: 'italic' }}>
                  +{overflowRows.toLocaleString()} more capabilities not shown — collapse a branch above to bring
                  others into view, or use Inventory filtered to Business Capability to see every record.
                </Typography>
              )}
            </Paper>

            <Paper sx={{ flex: 1, minWidth: 300, p: 2 }}>
              {!selected ? (
                <Typography variant="body2" sx={{ color: GREY }}>
                  Select a capability to see what maps to it.
                </Typography>
              ) : (
                <>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <TypeBadge type="BusinessCapability" size={20} />
                    <Typography sx={{ fontWeight: 700, color: HEADER, fontSize: 15 }}>{selected.name}</Typography>
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title="Open fact sheet to edit, map applications, or set its parent">
                      <IconButton size="small" onClick={() => onOpen(selected.id)}>
                        <OpenInNewIcon fontSize="small" sx={{ color: BLUE }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  {selected.fields?.description && (
                    <Typography variant="body2" sx={{ color: GREY, mb: 1.5 }}>{selected.fields.description}</Typography>
                  )}

                  {!!selectedChildren.length && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="overline" sx={{ color: HEADER, fontWeight: 700 }}>
                        Child capabilities ({selectedChildren.length})
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                        {selectedChildren.map((c) => (
                          <Chip key={c.id} size="small" label={c.name} onClick={() => setSelectedId(c.id)}
                            sx={{ cursor: 'pointer' }} />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  <Divider sx={{ my: 1.5 }} />

                  <Typography variant="overline" sx={{ color: HEADER, fontWeight: 700 }}>
                    Directly mapped applications ({selectedDirect.length})
                  </Typography>
                  {selectedDirect.length ? (
                    <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5, mb: 1.5 }}>
                      {selectedDirect.slice(0, MAX_APPS_IN_PANEL).map((a) => <AppChip key={a.id} app={a} onOpen={onOpen} />)}
                      {selectedDirect.length > MAX_APPS_IN_PANEL && (
                        <Typography variant="caption" sx={{ color: GREY }}>
                          +{selectedDirect.length - MAX_APPS_IN_PANEL} more
                        </Typography>
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="body2" sx={{ color: GREY, fontStyle: 'italic', mb: 1.5 }}>
                      (none mapped) — map an application to this capability from the application&rsquo;s own fact
                      sheet (Business Support → Business Capabilities).
                    </Typography>
                  )}

                  {!!selectedChildren.length && (
                    <>
                      <Typography variant="overline" sx={{ color: HEADER, fontWeight: 700 }}>
                        Rolled up from children ({Math.max(0, selectedRollup.length - selectedDirect.length)})
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                        {selectedRollup
                          .filter((a) => !selectedDirect.some((d) => d.id === a.id))
                          .slice(0, MAX_APPS_IN_PANEL)
                          .map((a) => <AppChip key={a.id} app={a} onOpen={onOpen} />)}
                      </Stack>
                    </>
                  )}
                </>
              )}
            </Paper>
          </Box>

          {/* Unmapped applications — the work queue. Always visible, never
              behind a click a user has to know to make. */}
          <Paper sx={{ mt: 2, p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontWeight: 700, color: HEADER, fontSize: 14 }}>
                Unmapped applications
              </Typography>
              <Chip size="small" label={unmappedApps.length}
                sx={{ height: 20, fontWeight: 700, bgcolor: unmappedApps.length ? '#fdecea' : '#eef4fb', color: unmappedApps.length ? '#8a2118' : HEADER }} />
            </Stack>
            <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 1 }}>
              Applications in this workspace with no Business Capability relation at all — the list of what still
              needs to be mapped, not a footnote.
            </Typography>
            {!unmappedApps.length ? (
              <Typography variant="body2" sx={{ color: '#2E7D32', fontWeight: 600 }}>
                Every application in this workspace maps to at least one capability.
              </Typography>
            ) : (
              <>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {unmappedApps.slice(0, unmappedLimit).map((a) => <AppChip key={a.id} app={a} onOpen={onOpen} />)}
                </Stack>
                {unmappedApps.length > unmappedLimit && (
                  <Button size="small" sx={{ mt: 1 }} onClick={() => setUnmappedLimit((n) => n + MAX_UNMAPPED_SHOWN)}>
                    Show {Math.min(MAX_UNMAPPED_SHOWN, unmappedApps.length - unmappedLimit)} more
                    &nbsp;({(unmappedApps.length - unmappedLimit).toLocaleString()} remaining)
                  </Button>
                )}
              </>
            )}
          </Paper>
        </>
      )}

      {/* New capability — a draft fact sheet via the normal create path, then
          straight into FactSheetDetail to name/describe/map it. Mirrors
          Inventory's "New". */}
      <Dialog open={newOpen} onClose={() => setNewOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>New capability</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" label="Name" sx={{ mt: 1 }}
            value={newName} onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim()) createCapability(); }} />
          <Typography variant="caption" sx={{ color: GREY, mt: 1, display: 'block' }}>
            Opens the new fact sheet so you can set its description and parent capability.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!newName.trim()} onClick={createCapability}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Starter set — one confirm dialog, explicit about what it is. */}
      <Dialog open={starterOpen} onClose={() => (!seeding && setStarterOpen(false))} fullWidth maxWidth="sm">
        <DialogTitle>Start from a healthcare capability set</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This creates {STARTER_CAPABILITIES.length} Business Capability fact sheets in this workspace — a
            common, generic starting point for a US health system (Patient Care, Revenue Cycle, Clinical
            Ancillaries, ERP &amp; Corporate Services, Population Health, Access &amp; Engagement, IT &amp;
            Security, and a handful of children under each).
          </Typography>
          <Typography variant="body2" sx={{ color: GREY, mb: 1.5 }}>
            It is a <strong>starting point</strong>, not a prescription — rename, reparent, merge or delete any of
            it afterward exactly like any other fact sheet. Only offered because this workspace currently has zero
            Business Capability fact sheets.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {STARTER_CAPABILITIES.filter((c) => !c.parent).map((c) => (
              <Chip key={c.name} size="small" label={c.name} sx={{ bgcolor: '#eef4fb' }} />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStarterOpen(false)} disabled={seeding}>Cancel</Button>
          <Button variant="contained" disabled={seeding} sx={{ bgcolor: NAVY, '&:hover': { bgcolor: BLUE } }} onClick={seedStarter}>
            {seeding ? 'Creating…' : `Create ${STARTER_CAPABILITIES.length} capabilities`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
